import {
  createSlice,
  createAsyncThunk,
  createDraftSafeSelector,
} from '@reduxjs/toolkit';
import { DEFAULT_PROJECT_UUID } from '../../../constants';

import { api } from '../../api/api';

export const loadProjects = createAsyncThunk(
  'project/loadProjects',
  async () => {
    const response = await api.projects();
    return response;
  },
);

export const loadUserPermissions = createAsyncThunk(
  'project/loadUserPermissions',
  async () => {
    const response = await api.userPermissions();
    return response;
  },
);

// selectors
const selectSelf = (state) => state.project;
export const selectProjects = createDraftSafeSelector(
  selectSelf,
  (project) => project.projects,
);
export const selectProjectAlias = createDraftSafeSelector(
  selectSelf,
  (project) => project.projectAlias,
);
export const selectProjectUuid = createDraftSafeSelector(
  selectSelf,
  (project) => project.projectUuid,
);
export const selectProject = createDraftSafeSelector(selectSelf, (project) => {
  return (
    project.projects.find((proj) => proj.uuid === project.projectUuid) || null
  );
});
export const selectProjectsLoading = createDraftSafeSelector(
  selectSelf,
  (project) => project.loading,
);
export const selectUserEmail = createDraftSafeSelector(
  selectSelf,
  (project) => project.userEmail,
);

// New permission selectors
export const selectIsAdmin = createDraftSafeSelector(
  selectSelf,
  (project) => project.isAdmin,
);
export const selectUserProjects = createDraftSafeSelector(
  selectSelf,
  (project) => project.userProjects,
);
export const selectCurrentProjectRole = createDraftSafeSelector(
  [selectSelf, selectProjectUuid],
  (project, projectUuid) => {
    const userProject = project.userProjects.find(
      (p) => p.uuid === projectUuid,
    );
    return userProject ? userProject.role : null;
  },
);
// Raw capability flags, exactly as /current-user reported them. The "admins have it
// implicitly" rule is applied in usePermission(), the one place permissions compose.
export const selectCanManageSharedConfigs = createDraftSafeSelector(
  selectSelf,
  (project) => project.canManageSharedConfigs,
);
export const selectCanManageSharedFields = createDraftSafeSelector(
  selectSelf,
  (project) => project.canManageSharedFields,
);
// True once /current-user has answered (or failed). Gated screens render a loading
// state instead of a wrong one — permissions land after first paint.
export const selectPermissionsLoaded = createDraftSafeSelector(
  selectSelf,
  (project) => project.permissionsLoaded,
);

const projectSlice = createSlice({
  name: 'project',
  initialState: {
    projectUuid: '',
    projectAlias: '',
    projects: [],
    loading: false,
    userEmail: '',
    // New permission fields
    isAdmin: false,
    canManageSharedConfigs: false,
    canManageSharedFields: false,
    permissionsLoaded: false,
    userProjects: [], // { uuid, name, role }
  },
  reducers: {
    changeProject(state, action) {
      state.projectUuid = action.payload;
      state.projects.forEach((project) => {
        if (action.payload == project.uuid) {
          state.projectAlias = project.alias;
        }
      });
    },
  },
  extraReducers: {
    [loadProjects.pending]: (state, action) => {
      state.loading = true;
      state.projects = [];
    },
    [loadProjects.fulfilled]: (state, action) => {
      state.loading = false;
      state.projects = action.payload.results;
      if (state.projectUuid) {
        // update the project alias
        const project = state.projects.find(
          ({ uuid }) => uuid === state.projectUuid,
        );
        state.projectAlias = project ? project.alias : '';
      }
    },
    [loadProjects.rejected]: (state, action) => {
      state.loading = false;
    },
    [loadUserPermissions.fulfilled]: (state, action) => {
      // New permission model: { is_admin, can_manage_shared_configs,
      // can_manage_shared_fields, email, projects: [{ uuid, name, role }] }
      state.isAdmin = action.payload.is_admin || false;
      // `|| false` so an older API that predates these columns reads as "no grant"
      // rather than leaking undefined into checked/disabled props.
      state.canManageSharedConfigs =
        action.payload.can_manage_shared_configs || false;
      state.canManageSharedFields =
        action.payload.can_manage_shared_fields || false;
      state.permissionsLoaded = true;
      state.userEmail = action.payload.email;
      state.userProjects = action.payload.projects || [];

      // Also populate projects from userProjects for compatibility
      if (action.payload.projects && action.payload.projects.length > 0) {
        state.projects = action.payload.projects.map((p) => ({
          uuid: p.uuid,
          alias: p.name,
          role: p.role,
        }));
      }
    },
    [loadUserPermissions.rejected]: (state) => {
      // Don't leave permission-gated screens stuck on a spinner if /current-user fails.
      state.permissionsLoaded = true;
    },
  },
});

export const { changeProject } = projectSlice.actions;

export default projectSlice.reducer;
